import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  Badge,
  Box,
  Button,
  CollectionPreferences,
  Header,
  Pagination,
  Table,
  TableProps,
  TextFilter,
  Toggle,
} from "@amzn/awsui-components-console";
import React, { useMemo, useState } from "react";

import { TablePreferences } from "@/components/chat/types";
import useAdminUserShareAccess from "@/hooks/admin/useAdminUserShareAccess";
import { UserFromApi } from "@/types/user";
import {
  ADMIN_DEFAULT_PAGE_SIZE,
  adminPageSizePreference,
} from "@/utils/adminTable";

import EmptyState from "../EmptyState";

const displayName = (u: UserFromApi): string =>
  u.name || [u.givenName, u.familyName].filter(Boolean).join(" ") || u.username;

/**
 * Order users by the name the table shows, not by the username underneath it.
 *
 * Declared at module scope so the column definition and the collection's default
 * sorting state pass the *same* function object: Cloudscape matches the active
 * sort to a column by comparator identity, so a per-render arrow would leave the
 * sorted column unmarked and log a warning.
 *
 * `localeCompare` with no options, matching the comparator Cloudscape builds for
 * a plain `sortingField`, so every admin table orders text the same way.
 */
export const compareByDisplayName = (a: UserFromApi, b: UserFromApi): number =>
  displayName(a).localeCompare(displayName(b));

const UsersAdmin: React.FC = () => {
  const { rows, loading, loadUsers, setShareEnabled } = useAdminUserShareAccess();

  // State, not a ref: the page size is rendered — it decides how many rows the
  // table shows — so changing it has to re-render.
  const [preferences, setPreferences] = useState<TablePreferences>({
    pageSize: ADMIN_DEFAULT_PAGE_SIZE,
  });

  const enabledCount = useMemo(
    () => rows.filter((r) => r.shareEnabled !== false).length,
    [rows]
  );

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps,
  } = useCollection(rows, {
    filtering: {
      empty: <EmptyState title="No users available" />,
      noMatch: (
        <EmptyState
          title="No matches"
          action={<Button onClick={() => actions.setFiltering("")}>Clear filter</Button>}
        />
      ),
      filteringFunction: (user: UserFromApi, text: string) => {
        const q = text.toLowerCase();
        return (
          displayName(user).toLowerCase().includes(q) ||
          user.username.toLowerCase().includes(q)
        );
      },
    },
    pagination: {
      // Show a fuller page by default; a 20-row page made an admin looking for
      // one user page through the whole directory.
      pageSize: preferences.pageSize,
    },
    // Ordered by the displayed name. The comparator is the module-level constant,
    // referenced here and on the "User" column, so Cloudscape can match this
    // active sort back to that column. `isDescending` is omitted, which is
    // ascending.
    sorting: {
      defaultState: {
        sortingColumn: { sortingComparator: compareByDisplayName },
      },
    },
  });

  const columnDefinitions = useMemo<TableProps<UserFromApi>["columnDefinitions"]>(
    () => [
      {
        id: "name",
        header: "User",
        cell: (user) => <Box variant="strong">{displayName(user)}</Box>,
        // The same function object the default sorting state uses, which is
        // what marks this header as sorted and makes the header clickable so
        // the default can be overridden.
        sortingComparator: compareByDisplayName,
      },
      {
        id: "shareStatus",
        header: "Share access",
        cell: (user) =>
          user.shareEnabled !== false ? (
            <Badge color="green">Enabled</Badge>
          ) : (
            <Badge color="red">Disabled</Badge>
          ),
      },
      {
        id: "shareToggle",
        header: "Can share",
        cell: (user) => (
          <Toggle
            checked={user.shareEnabled !== false}
            onChange={({ detail }) => setShareEnabled(user.username, detail.checked)}
            ariaLabel={`Share access for ${displayName(user)}`}
          />
        ),
      },
    ],
    [setShareEnabled]
  );

  return (
    <Table
      {...collectionProps}
      data-testid="users-share-access-table"
      variant="container"
      columnDefinitions={columnDefinitions}
      items={items}
      loading={loading}
      loadingText="Loading users"
      trackBy="username"
      empty={<EmptyState title="No users available" />}
      header={
        <Header
          counter={`(${rows.length})`}
          description={`${enabledCount} can share · ${rows.length - enabledCount} disabled`}
          actions={
            <Button iconName="refresh" onClick={loadUsers} ariaLabel="Refresh users">
              Refresh
            </Button>
          }
        >
          User Share Access
        </Header>
      }
      filter={
        <TextFilter
          {...filterProps}
          filteringPlaceholder="Search by name or username"
          countText={filteredItemsCount !== undefined ? `${filteredItemsCount} matches` : ""}
        />
      }
      pagination={<Pagination {...paginationProps} />}
      preferences={
        <CollectionPreferences
          title="Preferences"
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          preferences={preferences}
          pageSizePreference={adminPageSizePreference()}
          onConfirm={({ detail }) =>
            setPreferences({
              pageSize: detail.pageSize ?? ADMIN_DEFAULT_PAGE_SIZE,
            })
          }
        />
      }
    />
  );
};

export default UsersAdmin;
